import { useSyncExternalStore } from "react";
import { useQuery } from "@tanstack/react-query";

import { useAuth } from "@/contexts/AuthContext";
import { operatorApi, type OperatorMe } from "@/lib/api";
import { getStaffOrgSelection, subscribeStaffOrgSelection } from "@/lib/staffOrgSelection";
import { verticalFrom, type Vertical } from "@/lib/verticalCopy";

/**
 * The staff org selection as React state: re-renders whoever reads it when a
 * staff member switches org. `null` for everyone who is not switched, which is
 * every customer operator.
 */
export function useStaffOrgSelection(): string | null {
  return useSyncExternalStore(subscribeStaffOrgSelection, getStaffOrgSelection, () => null);
}

/**
 * The logged-in operator's membership — `GET /api/v1/operator/me`.
 *
 * Extracted because three callers now need it and the query options have to
 * agree exactly: the key, the staleTime and the gcTime are what make the
 * second and third callers cache reads rather than extra requests. That
 * contract used to be maintained by copying the `useQuery` block, which is
 * fine until one copy drifts.
 *
 * Keyed by user id so it refetches on a user switch; the cache is cleared on
 * sign-out (AuthContext). Keyed by the staff org selection too, so a switch
 * refetches: the server answers with the SELECTED org as `org`, and with it
 * the vertical, the subdomain and the flow picker follow the switch with no
 * change of their own.
 *
 * ⚠️ `ProtectedRoute` is the one caller that genuinely *fetches* this. Every
 * other caller renders inside it, so by the time they mount the key is already
 * populated. Calling this outside a `ProtectedRoute` subtree issues a real
 * request — which is why the public journeys do not use it (see below).
 */
export function useOperatorMe() {
  const { user } = useAuth();
  const selection = useStaffOrgSelection();

  return useQuery({
    queryKey: ["operator-me", user?.id, selection],
    queryFn: () => operatorApi.me(),
    enabled: !!user,
    staleTime: 5 * 60_000,
    gcTime: 5 * 60_000,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

/**
 * The operator's vertical, and whether it is known yet.
 *
 * ## Why this returns a loading flag instead of just a vertical
 *
 * `verticalFrom()` resolves absent to `"unknown"` (leasing copy, no rating),
 * which is the right *render* for an unknown org, but a caller that can wait
 * should wait rather than paint that and then swap it. A recruiter watching the dashboard
 * re-word itself mid-load is worse than a beat of nothing, and it is exactly
 * what a bare fallback would produce on a cold cache.
 *
 * So: `vertical` is always safe to render, and `isResolved` says whether it
 * reflects a real answer. Callers that redirect (`OperatorHomeRedirect`) or
 * re-word a whole surface should gate on `isResolved`; callers picking a
 * single word can ignore it.
 *
 * ## Operator surfaces only
 *
 * The public journeys — `/verify/:token` and `/voucher-flow` — have no
 * operator membership to resolve. They read `org_industry` off the session
 * status payload instead, which is the same fact arriving by the only route
 * available to a magic-link visitor.
 *
 * ## Follows the staff org switch
 *
 * `org.industry` is the SELECTED org's industry while a staff member is
 * switched (the server decides that; see `OperatorMe.staff`), so a leasing
 * staff member viewing a recruiting org gets recruiting wording and no rating
 * surfaces, exactly as that org's own recruiters do.
 */
export function useOperatorVertical(): {
  vertical: Vertical;
  isResolved: boolean;
  me: OperatorMe | undefined;
} {
  const query = useOperatorMe();

  return {
    vertical: verticalFrom(query.data?.org?.industry),
    // `isLoading`, not `isPending`: in react-query v5 a *disabled* query
    // (no signed-in user) stays `pending` forever, so `!isPending` would
    // never resolve on a logged-out surface. `isLoading` is `pending &&
    // fetching`, i.e. "a request is genuinely in flight" — which is also the
    // gate ProtectedRoute and OperatorHomeRedirect already use.
    //
    // An *error* counts as resolved: ProtectedRoute renders its own gate on a
    // failed `me()`, so a caller seeing one here is in a race, not a stuck
    // state, and `"unknown"` (no rating) is the safe landing either way.
    isResolved: !query.isLoading,
    me: query.data,
  };
}
